import { GiftCardTransactionsFragment } from '@/gql/generated';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatNumberToCurrency, formatPhoneNumber } from '@/lib/utils';
import { TPrinterConfig } from '@/types/printer/printer.type';
import { paymentMethods, transactionsType } from '@/utils/constant';
import { formatCodeGiftCard } from '@/utils/gift-card';
import { transforms } from '@react-thermal-printer/image';
import * as Sentry from '@sentry/react';
import { GetStorageItem, PrintInput } from 'gap-miniapp-sdk';
import isEmpty from 'lodash/isEmpty';
import { createContext, useContext } from 'react';
import {
  Br,
  Cut,
  Image,
  Line,
  Printer,
  render,
  Row,
  Text,
} from 'react-thermal-printer';
import { AuthenticatedContext } from '../(authenticated)/_provider/authenticated-provider';
import { useHandleToast } from '../(authenticated)/hooks/use-toast';
import { GapSdkContext } from './gap-sdk-provider';

type TGapPrinterSdkContext = {
  event: {
    onPrintReceiptByTransaction: (
      transaction: GiftCardTransactionsFragment,
    ) => void;
  };
};

export const GapPrinterSdkContext = createContext<TGapPrinterSdkContext>({
  event: {
    onPrintReceiptByTransaction: () => {},
  },
});

export default function GapPrinterSdkProvider(props: {
  children?: React.ReactNode;
}) {
  const { gapMiniAppSdk } = useContext(GapSdkContext);
  const { crmCompany, user } = useContext(AuthenticatedContext);

  const { showErrorToast } = useHandleToast();

  const initConfigPrinter = async (): Promise<PrintInput | null> => {
    const fastConnectPrinterStorage: GetStorageItem = {
      appId: 'fast-connect',
      key: 'PRINTER_CONFIG',
    };

    const printerConfig = await gapMiniAppSdk?.getStorageItem(
      fastConnectPrinterStorage,
    );

    if (typeof printerConfig === 'object' && printerConfig) {
      const config = JSON.parse(
        JSON.stringify(printerConfig),
      ) as TPrinterConfig;

      const inputData: PrintInput = {
        ip: config.value?.ip ?? '',
        port: 9100,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: config.value.type as any,
        data: '',
      };
      if (config.value.type === 'usb') {
        inputData.deviceName = config.value.deviceName;
        inputData.productId = config.value.productId;
        inputData.vendorId = config.value.vendorId;
      }
      if (config.value.type === 'bluetooth') {
        inputData.address = config.value.address;
      }

      if (
        config.value.type === 'network' &&
        config.value?.ip &&
        config.value?.ip?.length === 0
      ) {
        showErrorToast('IP Printer not found!');
        return null;
      }
      return inputData;
    }
    return null;
  };

  const RenderReceiptCash = (transaction: GiftCardTransactionsFragment) => {
    const receipt = (
      <Printer type="epson">
        <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
          {crmCompany?.name}
        </Text>
        <Text align="center">{crmCompany?.address}</Text>
        <Text align="center">{formatPhoneNumber(crmCompany?.phone ?? '')}</Text>
        <Br />
        <Text align="left" bold={true}>
          Transaction ID: {transaction.code}
        </Text>
        <Text align="left">
          DateTime:{' '}
          {formatDateToTimezone(
            transaction.date,
            user?.time_zone!,
            'MM/dd/yyyy hh:mm a',
          )}
        </Text>
        <Line />
        <Row
          left={
            <>
              Gift Card :{' '}
              {transaction.gift_card?.pos_gift_card?.code
                ? formatCodeGiftCard(
                    transaction.gift_card?.pos_gift_card?.code,
                    false,
                  )
                : formatCodeGiftCard(transaction?.gift_card?.code ?? '', true)}
            </>
          }
          right={<></>}
        />
        <Row
          left={<>Amount</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Row
          left={<>TOTAL</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Br />
        <Line />
        <Text align="center">Payment Details</Text>
        <Text align="left">Action: Cash</Text>
        <Text align="left">Status: Cashed</Text>
        <Cut partial={true} />
      </Printer>
    );
    return receipt;
  };

  const RenderReceiptRedeem = (transaction: GiftCardTransactionsFragment) => {
    const receipt = (
      <Printer type="epson">
        <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
          {crmCompany?.name}
        </Text>
        <Text align="center">{crmCompany?.address}</Text>
        <Text align="center">{formatPhoneNumber(crmCompany?.phone ?? '')}</Text>
        <Br />
        <Text align="left" bold={true}>
          Transaction ID: {transaction.code}
        </Text>
        <Text align="left">
          DateTime:{' '}
          {formatDateToTimezone(
            transaction.date,
            user?.time_zone!,
            'MM/dd/yyyy hh:mm a',
          )}
        </Text>
        <Line />
        <Row
          left={
            <>
              Gift Card :{' '}
              {transaction.gift_card?.pos_gift_card?.code
                ? formatCodeGiftCard(
                    transaction.gift_card?.pos_gift_card?.code,
                    false,
                  )
                : formatCodeGiftCard(transaction?.gift_card?.code ?? '', true)}
            </>
          }
          right={<></>}
        />
        <Row
          left={<>Amount</>}
          right={<>{formatNumberToCurrency(transaction?.balance_change)}</>}
        />
        <Row
          left={<>TOTAL</>}
          right={<>{formatNumberToCurrency(transaction?.balance_change)}</>}
        />
        <Br />
        <Line />
        <Text align="center">Payment Details</Text>
        <Text align="left">Action: Gift Card</Text>
        <Text align="left">Status: Redemption</Text>
        <Cut partial={true} />
      </Printer>
    );
    return receipt;
  };

  const RenderReceiptCharge = (transaction: GiftCardTransactionsFragment) => {
    const receipt = (
      <Printer type="epson">
        <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
          {crmCompany?.name}
        </Text>
        <Text align="center">{crmCompany?.address}</Text>
        <Text align="center">{formatPhoneNumber(crmCompany?.phone ?? '')}</Text>
        <Br />
        <Text align="left" bold={true}>
          Transaction ID: {transaction.code}
        </Text>
        <Text align="left">
          DateTime:{' '}
          {formatDateToTimezone(
            transaction.date,
            user?.time_zone!,
            'MM/dd/yyyy hh:mm a',
          )}
        </Text>
        <Line />
        <Row
          left={
            <>
              Gift Card :{' '}
              {transaction.gift_card?.pos_gift_card?.code
                ? formatCodeGiftCard(
                    transaction.gift_card?.pos_gift_card?.code,
                    false,
                  )
                : formatCodeGiftCard(transaction?.gift_card?.code ?? '', true)}
            </>
          }
          right={<></>}
        />
        <Row
          left={<>Amount</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Row
          left={<>TOTAL</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Br />
        <Line />
        <Text align="center">Payment Details</Text>
        <Text align="left">
          {`Action: ${
            transaction.payment_methods[0]?.action?.replace(
              /^./,
              (char: string) => char.toUpperCase(),
            ) ?? ''
          }`}
        </Text>
        <Text align="left">Status: Authorized</Text>
        <Line />
        <Text align="center">Payment Method</Text>
        <Text align="left">
          Cardholder name:
          {transaction.payment_methods[0]?.card_info?.cardholder_name}
        </Text>
        <Text align="left">
          Card type: {transaction.payment_methods[0]?.card_info?.card_type}
        </Text>
        <Text align="left">
          Card last four:{' '}
          {transaction.payment_methods[0]?.card_info?.card_number_last_four}
        </Text>
        <Line />
        <Text align="center">Transactions</Text>
        <Text align="left">
          Auth code: {transaction.payment_methods[0]?.auth_code}
        </Text>
        <Text align="left">
          Trans Id: {transaction.payment_methods[0]?.reference_id}
        </Text>
        <Text align="left">
          Trans message: {transaction?.payment_methods[0]?.trans_message}
        </Text>
        <Line />
        <Text align="center">Device</Text>
        <Text align="left">
          Serial number: {transaction.payment_methods[0]?.device_sn}
        </Text>
        <Text align="left">
          ID: {transaction.payment_methods[0]?.device_id}{' '}
        </Text>
        <Br />
        <Text align="left">Signature: </Text>
        <Br />
        <Br />
        {isEmpty(transaction.signature?.base_uri) ||
          (transaction.signature?.base_uri && (
            <Image
              width={300}
              height={150}
              src={transaction.signature.base_uri}
              alt={transaction.signature?.name}
              transforms={[transforms.floydSteinberg]}
            />
          ))}
        <Br />
        <Br />
        <Cut partial={true} />
      </Printer>
    );
    return receipt;
  };

  const RenderReceiptManualCardEntry = (
    transaction: GiftCardTransactionsFragment,
  ) => {
    const receipt = (
      <Printer type="epson">
        <Text align="center" bold={true} size={{ width: 2, height: 2 }}>
          {crmCompany?.name}
        </Text>
        <Text align="center">{crmCompany?.address}</Text>
        <Text align="center">{formatPhoneNumber(crmCompany?.phone ?? '')}</Text>
        <Br />
        <Text align="left" bold={true}>
          Transaction ID: {transaction.code}
        </Text>
        <Text align="left">
          DateTime:{' '}
          {formatDateToTimezone(
            transaction.date,
            user?.time_zone!,
            'MM/dd/yyyy hh:mm a',
          )}
        </Text>
        <Line />
        <Row
          left={
            <>
              Gift Card :{' '}
              {transaction.gift_card?.pos_gift_card?.code
                ? formatCodeGiftCard(
                    transaction.gift_card?.pos_gift_card?.code,
                    false,
                  )
                : formatCodeGiftCard(transaction?.gift_card?.code ?? '', true)}
            </>
          }
          right={<></>}
        />
        <Row
          left={<>Amount</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Row
          left={<>TOTAL</>}
          right={<>{formatNumberToCurrency(transaction?.price)}</>}
        />
        <Br />
        <Line />
        <Text align="center">Payment Details</Text>
        <Text align="left">{`Action: Manually Entered`}</Text>
        <Text align="left">Status: Sale</Text>
        <Line />
        <Text align="center">Payment Method</Text>
        <Text align="left">
          Expiration date:{' '}
          {formatDateToTimezone(
            transaction.payment_methods[0]?.card_info?.expiration_date ?? '',
            user?.time_zone!,
            'MM/yyyy',
          )}
        </Text>
        <Text align="left">
          Card last four:{' '}
          {transaction.payment_methods[0]?.card_info?.card_number_last_four}
        </Text>
        <Line />
        <Text align="center">Transactions</Text>
        <Text align="left">
          Auth code: {transaction.payment_methods[0]?.auth_code}
        </Text>
        <Text align="left">
          Trans Id: {transaction.payment_methods[0]?.reference_id}
        </Text>
        <Text align="left">
          Trans message: {transaction?.payment_methods[0]?.trans_message}
        </Text>

        <Br />
        <Text align="left">Signature: </Text>
        <Br />
        <Br />
        {isEmpty(transaction.signature?.base_uri) ||
          (transaction.signature?.base_uri && (
            <Image
              width={300}
              height={150}
              src={transaction.signature.base_uri}
              alt={transaction.signature?.name}
              transforms={[transforms.floydSteinberg]}
            />
          ))}
        <Br />
        <Br />
        <Cut partial={true} />
      </Printer>
    );
    return receipt;
  };

  const onPrintReceiptByTransaction = async (
    transaction: GiftCardTransactionsFragment,
  ) => {
    try {
      const getInput = await initConfigPrinter();
      if (getInput) {
        let receipt = null;
        if (
          transaction.type === transactionsType.TOP_UP &&
          transaction.payment_methods[0]?.type === paymentMethods.CASH
        ) {
          receipt = RenderReceiptCash(transaction);
        }
        if (
          transaction.type === transactionsType.TOP_UP &&
          transaction.payment_methods[0]?.type === paymentMethods.CHARGE
        ) {
          receipt = RenderReceiptCharge(transaction);
        }

        if (
          transaction.type === transactionsType.TOP_UP &&
          transaction.payment_methods[0]?.type ===
            paymentMethods.MANUAL_CARD_ENTRY
        ) {
          receipt = RenderReceiptManualCardEntry(transaction);
        }

        if (transaction.type === transactionsType.REDEMPTION) {
          receipt = RenderReceiptRedeem(transaction);
        }
        if (!receipt) return;

        const data: Uint8Array = await render(receipt);
        gapMiniAppSdk?.printEscPos({
          ...getInput,
          data: data.toString(),
        });
      }
    } catch (error) {
      showErrorToast('The receipt was not printed!');
      Sentry.captureException(error, {
        extra: {
          type: 'Printer',
        },
      });
    }
  };

  return (
    <GapPrinterSdkContext.Provider
      value={{
        event: {
          onPrintReceiptByTransaction: onPrintReceiptByTransaction,
        },
      }}
    >
      {props.children}
    </GapPrinterSdkContext.Provider>
  );
}
